 
import React, { useCallback, useEffect, useState } from 'react';
import { getMyWorkEntries, updateMyWorkEntry } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ENTRY_STATUS, formatAmount, formatDate } from '../../lib/workStatus';
import { toast } from 'sonner';

const EmployeeEntries = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: '', amount: '', completed: false, note: '' });

  const load = useCallback(async () => {
    try {
      setEntries(await getMyWorkEntries());
    } catch (error) {
      toast.error('Neizdevās ielādēt atskaites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (entry) => {
    setEditing(entry);
    setForm({ date: entry.date.split('T')[0], amount: entry.amount, completed: entry.completed, note: entry.note || '' });
  };

  const save = async () => {
    try {
      await updateMyWorkEntry(editing.id, { date: `${form.date}T00:00:00`, amount: Number(form.amount), completed: form.completed, note: form.note || null });
      toast.success('Atskaite atjaunināta');
      setEditing(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās saglabāt');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="employee-entries-loading"><div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="employee-entries-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1F3D2B]" data-testid="employee-entries-title">Manas atskaites</h1>
        <p className="text-[#2E2E2E]/70">Iesniegtos ierakstus var labot, kamēr tos nav apstiprinājis administrators.</p>
      </div>

      {entries.length === 0 ? (
        <Card className="border-0 shadow-sm"><CardContent className="py-10 text-center text-[#2E2E2E]/60" data-testid="employee-entries-empty">Vēl nav iesniegtu atskaišu.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => {
            const status = ENTRY_STATUS[entry.status] || { label: entry.status, className: '' };
            const editable = entry.status === 'submitted' || entry.status === 'needs_fix';
            return (
              <Card key={entry.id} className={`border-0 shadow-sm ${entry.status === 'needs_fix' ? 'ring-2 ring-amber-300' : ''}`} data-testid={`employee-entry-${entry.id}`}>
                <CardContent className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-[#2E2E2E]">{entry.task_title}</p>
                    <p className="text-sm text-[#2E2E2E]/60">{entry.project_name || '-'} · {entry.work_type_label || '-'}</p>
                    <p className="text-sm mt-1" data-testid={`employee-entry-amount-${entry.id}`}>
                      <strong>{formatDate(entry.date)}</strong> · {formatAmount(entry.amount, entry.unit)}{entry.completed ? ' · darbs pabeigts' : ''}
                    </p>
                    {entry.note && <p className="text-xs text-[#2E2E2E]/60 mt-1">{entry.note}</p>}
                    {entry.status === 'needs_fix' && entry.admin_comment && (
                      <p className="mt-2 text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2" data-testid={`employee-entry-comment-${entry.id}`}>Administrators: {entry.admin_comment}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={status.className} data-testid={`employee-entry-status-${entry.id}`}>{status.label}</Badge>
                    {editable && <Button size="sm" variant="outline" onClick={() => openEdit(entry)} data-testid={`employee-entry-edit-${entry.id}`}>Labot</Button>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="employee-entry-edit-dialog">
          <DialogHeader>
            <DialogTitle>Labot atskaiti</DialogTitle>
            <DialogDescription>{editing?.task_title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Datums</Label><Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} data-testid="employee-entry-edit-date" /></div>
              <div className="space-y-1"><Label>Apjoms ({editing?.unit})</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} data-testid="employee-entry-edit-amount" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.completed} onCheckedChange={(v) => setForm((p) => ({ ...p, completed: Boolean(v) }))} data-testid="employee-entry-edit-completed" /> Darbs pabeigts</label>
            <div className="space-y-1"><Label>Piezīme</Label><Input value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} data-testid="employee-entry-edit-note" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="employee-entry-edit-cancel">Atcelt</Button>
              <Button onClick={save} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="employee-entry-edit-save">Saglabāt</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeEntries;
